import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Upload, FileUp, Send } from 'lucide-react';
import { documentTypes } from '@/utils/documentTypes';
import { API } from '@/config/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';

const UPLOAD_DRAFT_FORM_KEY = 'udt_upload_draft_form';
const UPLOAD_DRAFT_DB_NAME = 'udt_upload_draft_db';
const UPLOAD_DRAFT_DB_STORE = 'file';
const UPLOAD_DRAFT_FILE_KEY = 'draft';

function getFormDraft() {
  try {
    const raw = sessionStorage.getItem(UPLOAD_DRAFT_FORM_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function setFormDraft(draft) {
  try {
    if (draft && (draft.docType || draft.purpose || draft.expiryValue)) {
      sessionStorage.setItem(UPLOAD_DRAFT_FORM_KEY, JSON.stringify(draft));
    } else {
      sessionStorage.removeItem(UPLOAD_DRAFT_FORM_KEY);
    }
  } catch {
    // ignore
  }
}

function clearFormDraft() {
  try {
    sessionStorage.removeItem(UPLOAD_DRAFT_FORM_KEY);
  } catch {
    // ignore
  }
}

function saveFileDraft(blob, fileName, mimeType) {
  return new Promise((resolve) => {
    const req = indexedDB.open(UPLOAD_DRAFT_DB_NAME, 1);
    req.onerror = () => resolve();
    req.onupgradeneeded = (e) => {
      if (!e.target.result.objectStoreNames.contains(UPLOAD_DRAFT_DB_STORE)) {
        e.target.result.createObjectStore(UPLOAD_DRAFT_DB_STORE);
      }
    };
    req.onsuccess = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(UPLOAD_DRAFT_DB_STORE)) {
        db.close();
        return resolve();
      }
      const tx = db.transaction(UPLOAD_DRAFT_DB_STORE, 'readwrite');
      const store = tx.objectStore(UPLOAD_DRAFT_DB_STORE);
      store.put({ blob, fileName, mimeType }, UPLOAD_DRAFT_FILE_KEY);
      db.close();
      resolve();
    };
  });
}

function loadFileDraft() {
  return new Promise((resolve) => {
    const req = indexedDB.open(UPLOAD_DRAFT_DB_NAME, 1);
    req.onerror = () => resolve(null);
    req.onsuccess = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(UPLOAD_DRAFT_DB_STORE)) {
        db.close();
        return resolve(null);
      }
      const tx = db.transaction(UPLOAD_DRAFT_DB_STORE, 'readonly');
      const getReq = tx.objectStore(UPLOAD_DRAFT_DB_STORE).get(UPLOAD_DRAFT_FILE_KEY);
      getReq.onsuccess = () => {
        const data = getReq.result;
        db.close();
        if (data?.blob != null && data?.fileName) {
          resolve(new File([data.blob], data.fileName, { type: data.mimeType || 'application/octet-stream' }));
        } else {
          resolve(null);
        }
      };
      getReq.onerror = () => {
        db.close();
        resolve(null);
      };
    };
    req.onupgradeneeded = (e) => {
      e.target.result.createObjectStore(UPLOAD_DRAFT_DB_STORE);
    };
  });
}

function clearFileDraft() {
  return new Promise((resolve) => {
    const req = indexedDB.open(UPLOAD_DRAFT_DB_NAME, 1);
    req.onerror = () => resolve();
    req.onsuccess = () => {
      const db = req.result;
      if (db.objectStoreNames.contains(UPLOAD_DRAFT_DB_STORE)) {
        const tx = db.transaction(UPLOAD_DRAFT_DB_STORE, 'readwrite');
        tx.objectStore(UPLOAD_DRAFT_DB_STORE).delete(UPLOAD_DRAFT_FILE_KEY);
      }
      db.close();
      resolve();
    };
    req.onupgradeneeded = (e) => {
      e.target.result.createObjectStore(UPLOAD_DRAFT_DB_STORE);
    };
  });
}

export default function UploadPage({ token, user }) {
  const navigate = useNavigate();
  const [file, setFileState] = useState(null);
  const [purpose, setPurpose] = useState('');
  const [docType, setDocType] = useState('');
  const [expiryUnit, setExpiryUnit] = useState('days');
  const [expiryValue, setExpiryValue] = useState('');
  const [uploading, setUploading] = useState(false);
  const [lastUploaded, setLastUploaded] = useState(null); // {udt_id, file_name}
  const [draftRestored, setDraftRestored] = useState(false);

  const setFile = (value) => {
    setFileState(value);
    if (value instanceof File) {
      saveFileDraft(value, value.name, value.type || '');
    } else {
      clearFileDraft();
    }
  };

  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [recipientQuery, setRecipientQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [shareMessage, setShareMessage] = useState('');
  const [shareExpiryUnit, setShareExpiryUnit] = useState('days');
  const [shareExpiryValue, setShareExpiryValue] = useState(1);
  const [sharePermissions, setSharePermissions] = useState('view_only');
  const [allowReshare, setAllowReshare] = useState(false);
  const [shareSubmitting, setShareSubmitting] = useState(false);
  const searchTimeoutRef = useRef(null);

  const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024; // 50MB

  // File magic bytes (signatures) for allowed types - detects corrupted/wrong-extension files
  const FILE_SIGNATURES = {
    pdf: [[0x25, 0x50, 0x44, 0x46]], // %PDF
    doc: [[0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1]], // OLE (DOC)
    docx: [[0x50, 0x4b, 0x03, 0x04], [0x50, 0x4b, 0x05, 0x06]], // ZIP (DOCX)
    jpg: [[0xff, 0xd8, 0xff]],
    jpeg: [[0xff, 0xd8, 0xff]],
    png: [[0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]],
  };

  // Restore draft form and file from sessionStorage/IndexedDB on mount
  useEffect(() => {
    if (draftRestored) return;
    const form = getFormDraft();
    if (form) {
      if (form.docType != null) setDocType(form.docType);
      if (form.purpose != null) setPurpose(form.purpose);
      if (form.expiryUnit != null) setExpiryUnit(form.expiryUnit);
      if (form.expiryValue != null) setExpiryValue(form.expiryValue);
    }
    loadFileDraft().then((restoredFile) => {
      if (restoredFile) setFileState(restoredFile);
      setDraftRestored(true);
    });
  }, [draftRestored]);

  // Persist form fields when they change (skip initial restore)
  useEffect(() => {
    if (!draftRestored) return;
    setFormDraft({ docType, purpose, expiryUnit, expiryValue });
  }, [draftRestored, docType, purpose, expiryUnit, expiryValue]);

  const validateFileIntegrity = (file) => {
    if (!file || file.size === 0) {
      return Promise.resolve({ valid: false, message: 'File is empty or corrupted.' });
    }
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    const signatures = FILE_SIGNATURES[ext];
    if (!signatures) {
      return Promise.resolve({ valid: false, message: 'File type not allowed. Use PDF, DOC, DOCX, JPG, or PNG.' });
    }
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const arr = new Uint8Array(e.target.result);
        const maxLen = Math.max(...signatures.map((sig) => sig.length), 8);
        if (arr.length < maxLen) {
          resolve({ valid: false, message: 'File appears corrupted or invalid.' });
          return;
        }
        const matches = signatures.some((sig) => sig.every((byte, i) => arr[i] === byte));
        resolve(
          matches
            ? { valid: true }
            : { valid: false, message: 'File does not match its extension or may be corrupted. Please upload a valid file.' }
        );
      };
      reader.onerror = () => resolve({ valid: false, message: 'Could not read file. It may be corrupted.' });
      reader.readAsArrayBuffer(file.slice(0, 12));
    });
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error('Please select a file');
      return;
    }

    if (!docType) {
      toast.error('Please select a document type');
      return;
    }

    const expiryNum = parseInt(expiryValue, 10);
    const maxExpiry = expiryUnit === 'days' ? 365 : 8760;
    if (!Number.isInteger(expiryNum) || expiryNum < 1 || expiryNum > maxExpiry) {
      toast.error('Please enter when the document expires (1 to ' + maxExpiry + ' ' + (expiryUnit === 'days' ? 'days' : 'hours') + ').');
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      toast.error('File size exceeds 50MB limit');
      return;
    }

    const integrity = await validateFileIntegrity(file);
    if (!integrity.valid) {
      toast.error(integrity.message || 'File is corrupted or invalid. Please choose a valid file.');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      const uploadExpiryHours = expiryUnit === 'days' ? Math.min(365, Math.max(1, expiryNum)) * 24 : Math.min(8760, Math.max(1, expiryNum));
      formData.append('file', file);
      formData.append('purpose', docType ? `${docType}${purpose ? ' - ' + purpose : ''}` : purpose);
      formData.append('expiry_hours', uploadExpiryHours);

      const response = await axios.post(`${API}/documents/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(`Document uploaded! UDT ID: ${response.data.udt_id}`);
      setLastUploaded({ udt_id: response.data.udt_id, file_name: file?.name || response.data.udt_id });
      setShareDialogOpen(true);
      clearFormDraft();
      clearFileDraft();
      setFileState(null);
      setPurpose('');
      setDocType('');
      setExpiryValue('');
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail.map((d) => d?.msg ?? d).join(', ')
          : 'Upload failed';
      toast.error(message);
    } finally {
      setUploading(false);
    }
  };

  const searchUsers = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/chat/users/search?query=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(res.data.users || []);
    } catch (error) {
      setSearchResults([]);
    }
  };

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => searchUsers(recipientQuery), 250);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [recipientQuery]);

  const submitShare = async () => {
    if (!lastUploaded?.udt_id) return;
    if (!selectedRecipient?.user_id) {
      toast.error('Please select a recipient');
      return;
    }
    try {
      setShareSubmitting(true);
      await axios.post(`${API}/share/direct`, {
        udt_id: lastUploaded.udt_id,
        recipient_id: selectedRecipient.user_id,
        message: shareMessage || `Shared: ${lastUploaded.file_name || lastUploaded.udt_id}`,
        expiry_hours: shareExpiryUnit === 'days' ? Math.min(365, Math.max(1, shareExpiryValue)) * 24 : Math.min(8760, Math.max(1, shareExpiryValue)),
        permissions: sharePermissions || 'view_only',
        allow_reshare: !!allowReshare
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Shared with ${selectedRecipient.name || 'recipient'}!`);
      setShareDialogOpen(false);
      navigate('/dashboard/documents');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Share failed');
    } finally {
      setShareSubmitting(false);
    }
  };

  return (
    <DashboardLayout user={user} token={token}>
      <div className="container mx-auto px-4 py-4 sm:py-6 lg:py-8 max-w-2xl w-full min-w-0">
        <div className="flex items-center gap-4 mb-4 sm:mb-6">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold" style={{ color: '#0F52BA' }}>Upload Document</h1>
            <p className="text-xs sm:text-sm" style={{ color: '#666' }}>Upload and secure your document with UDT</p>
          </div>
        </div>

        <div className="w-full">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileUp className="w-6 h-6" />
                Document Upload
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUpload} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="file">Select File *</Label>
                  <div className="border-2 border-dashed rounded-lg p-4 sm:p-6 lg:p-8 text-center"
                       style={{ borderColor: file ? '#10b981' : '#ddd' }}>
                    <Input
                      id="file"
                      type="file"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      onChange={async (e) => {
                        const selected = e.target.files[0];
                        if (!selected) return;
                        if (selected.size > MAX_FILE_SIZE_BYTES) {
                          toast.error('File size exceeds 50MB limit');
                          e.target.value = '';
                          return;
                        }
                        const integrity = await validateFileIntegrity(selected);
                        if (!integrity.valid) {
                          toast.error(integrity.message || 'File is corrupted or invalid. Please choose a valid file.');
                          e.target.value = '';
                          return;
                        }
                        setFile(selected);
                      }}
                      className="hidden"
                    />
                    <label htmlFor="file" className="cursor-pointer flex flex-col items-center">
                      <Upload className="w-12 h-12 mx-auto mb-2" style={{ color: file ? '#10b981' : '#ccc' }} />
                      <p
                        className="font-semibold max-w-full truncate"
                        style={{ color: file ? '#10b981' : '#666' }}
                      >
                        {file ? file.name : 'Click to upload or drag and drop'}
                      </p>
                      <p className="text-sm" style={{ color: '#999' }}>PDF, DOC, DOCX, JPG, PNG (Max 50MB)</p>
                    </label>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="docType">Document Type *</Label>
                  <Select value={docType} onValueChange={setDocType}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select document type" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px] overflow-y-auto">
                      {Object.entries(documentTypes).map(([category, types]) => (
                        <div key={category}>
                          <div className="px-2 py-1.5 text-sm font-semibold text-gray-500 bg-gray-50">
                            {category}
                          </div>
                          {types.map(type => (
                            <SelectItem key={type} value={type}>
                              {type}
                            </SelectItem>
                          ))}
                        </div>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="purpose">Purpose / Notes (Optional)</Label>
                  <Textarea
                    id="purpose"
                    placeholder="Additional notes about this document..."
                    value={purpose}
                    onChange={(e) => setPurpose(e.target.value)}
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Document Expires In *</Label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      onKeyDown={(e) => ['e', 'E', '+', '-', '.'].includes(e.key) && e.preventDefault()}
                      min="1"
                      max={expiryUnit === 'days' ? 365 : 8760}
                      placeholder="—"
                      value={expiryValue}
                      onChange={(e) => setExpiryValue(e.target.value)}
                      className="w-28"
                    />
                    <select
                      value={expiryUnit}
                      onChange={(e) => {
                        setExpiryUnit(e.target.value);
                        const n = parseInt(expiryValue, 10);
                        if (e.target.value === 'days' && !isNaN(n) && n > 365) setExpiryValue('365');
                        if (e.target.value === 'hours' && !isNaN(n) && n > 8760) setExpiryValue('8760');
                      }}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="days">Days</option>
                      <option value="hours">Hours</option>
                    </select>
                  </div>
                  <p className="text-xs" style={{ color: '#999' }}>
                    {expiryValue !== '' && !isNaN(parseInt(expiryValue, 10))
                      ? `Document will auto re-encrypt after ${expiryUnit === 'days'
                          ? `${expiryValue} day${Number(expiryValue) !== 1 ? 's' : ''}`
                          : `${expiryValue} hour${Number(expiryValue) !== 1 ? 's' : ''}`}`
                      : 'Enter when the document expires (1–365 days or 1–8760 hours).'}
                  </p>
                </div>

                <div className="flex gap-3">
                  <Button
                    type="submit"
                    disabled={uploading || !file}
                    className="flex-1"
                    style={{ background: uploading ? '#9ca3af' : '#0F52BA' }}
                  >
                    {uploading ? 'Uploading...' : 'Upload & Encrypt'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/dashboard/documents')}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>

        <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
          <DialogContent className="w-[calc(100vw-2rem)] max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex flex-wrap items-center justify-between gap-2 text-base sm:text-lg">
                <span className="min-w-0">Share your uploaded document</span>
                {lastUploaded?.udt_id && (
                  <Badge variant="outline" className="font-mono text-xs break-all">
                    {lastUploaded.udt_id}
                  </Badge>
                )}
              </DialogTitle>
            </DialogHeader>

            <div className="max-h-[70vh] overflow-y-auto space-y-4 pr-1 min-w-0">
              <div className="space-y-2">
                <Label className="text-sm">Recipient (mobile / email / name / UDT User ID)</Label>
                <Input
                  value={recipientQuery}
                  onChange={(e) => {
                    setRecipientQuery(e.target.value);
                    setSelectedRecipient(null);
                  }}
                  placeholder="Search recipient…"
                  className="w-full min-w-0"
                />
                {searchResults.length > 0 && !selectedRecipient && (
                  <div className="border rounded max-h-56 overflow-y-auto divide-y bg-white">
                    {searchResults.map((u) => (
                      <button
                        type="button"
                        key={u.user_id}
                        onClick={() => setSelectedRecipient(u)}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div>
                            <p className="font-semibold">{u.name}</p>
                            <p className="text-xs text-gray-600 break-all">{u.email}</p>
                          </div>
                          {u.udt_user_id && (
                            <Badge variant="outline" className="font-mono text-xs">
                              {u.udt_user_id}
                            </Badge>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {selectedRecipient && (
                  <div className="p-3 rounded border bg-gray-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 min-w-0">
                    <div className="min-w-0">
                      <p className="font-semibold text-sm break-words">{selectedRecipient.name}</p>
                      <p className="text-xs text-gray-600 break-all">{selectedRecipient.email}</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setSelectedRecipient(null)} className="flex-shrink-0">
                      Change
                    </Button>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-sm">Message (optional)</Label>
                <Input value={shareMessage} onChange={(e) => setShareMessage(e.target.value)} placeholder="Add a message…" className="w-full min-w-0" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-sm">Access Expires In</Label>
                  <div className="flex flex-wrap gap-2">
                    <Input
                      type="number"
                    onKeyDown={(e) => ['e', 'E', '+', '-', '.'].includes(e.key) && e.preventDefault()}
                      min="1"
                      max={shareExpiryUnit === 'days' ? 365 : 8760}
                      value={shareExpiryValue}
                      onChange={(e) => setShareExpiryValue(parseInt(e.target.value) || (shareExpiryUnit === 'days' ? 1 : 24))}
                      className="w-full min-w-0 sm:w-20"
                    />
                    <select
                      value={shareExpiryUnit}
                      onChange={(e) => {
                        setShareExpiryUnit(e.target.value);
                        if (e.target.value === 'days' && shareExpiryValue > 365) setShareExpiryValue(365);
                        if (e.target.value === 'hours' && shareExpiryValue > 8760) setShareExpiryValue(8760);
                      }}
                      className="w-full sm:w-auto min-w-0 px-2 py-2 border border-gray-300 rounded-md text-sm"
                    >
                      <option value="days">Days</option>
                      <option value="hours">Hours</option>
                    </select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Permissions</Label>
                  <select
                    value={sharePermissions || 'view_only'}
                    onChange={(e) => setSharePermissions(e.target.value)}
                    className="w-full min-w-0 px-3 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="view_only">View Only</option>
                    <option value="download_view">Download and View</option>
                    <option value="print_download_view">Print, Download and View</option>
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Reshare Permission</Label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300"
                    checked={allowReshare}
                    onChange={(e) => setAllowReshare(e.target.checked)}
                  />
                  <span>Allow recipient to reshare (forward) this document</span>
                </label>
              </div>

              <div className="flex flex-wrap justify-end gap-2">
                <Button variant="outline" onClick={() => navigate('/dashboard/documents')} className="flex-1 sm:flex-initial">
                  Skip
                </Button>
                <Button
                  onClick={submitShare}
                  disabled={!selectedRecipient || shareSubmitting}
                  style={{ background: '#0F52BA' }}
                  className="flex-1 sm:flex-initial flex items-center justify-center gap-2"
                >
                  {shareSubmitting ? (
                    <>
                      <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Sharing…</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      <span>Share</span>
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
